import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiAPI } from '../../utils/api';

const UploadListModal = ({ open, onClose }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0); // 0: Building kit, 1: Searching catalog, 2: Matching
  const [error, setError] = useState('');
  const inputRef = useRef();
  const navigate = useNavigate();

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleFile = (f) => {
    if (f.size > 5 * 1024 * 1024) { 
      setError('File must be under 5 MB'); 
      return; 
    }
    setFile(f);
    setError('');
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setLoadingStep(0);

    // Simulate progress steps
    const stepInterval = setInterval(() => {
      setLoadingStep(prev => prev < 2 ? prev + 1 : prev);
    }, 2500);

    try {
      const formData = new FormData();
      formData.append('list_image', file);
      
      const res = await aiAPI.uploadList(formData);
      clearInterval(stepInterval);
      
      const { matched_products, unmatched_items } = res.data.data;
      
      // Store data in sessionStorage to pass it to the CustomKitBuilderPage
      sessionStorage.setItem('customKitData', JSON.stringify({
        matched_products,
        unmatched_items
      }));

      onClose();
      navigate('/custom-kit');
    } catch (err) {
      clearInterval(stepInterval);
      setError(err.response?.data?.error || err.message || 'Upload failed. Please try again.');
      setLoading(false);
    }
  };

  const reset = () => {
    if (loading) return;
    setFile(null);
    setError('');
    onClose();
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      backdropFilter: 'blur(4px)'
    }} onClick={e => e.target === e.currentTarget && reset()}>
      <div style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth: 500,
        boxShadow: '0 25px 60px rgba(0,0,0,0.25)', overflow: 'hidden'
      }}>
        
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem', borderBottom: '1px solid #f3f4f6',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: '#1f2937' }}>
            Upload Your List
          </h3>
          <button onClick={reset} disabled={loading} style={{ 
            background: '#f3f4f6', border: 'none', borderRadius: '50%', color: '#4b5563', 
            width: 32, height: 32, cursor: loading ? 'not-allowed' : 'pointer', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '2rem 1.5rem', textAlign: 'center' }}>
          
          {loading ? (
            <div style={{ padding: '2rem 0' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1.5rem', animation: 'bounce 1.5s infinite' }}>📦</div>
              <h2 style={{ color: '#111827', fontWeight: 800, marginBottom: '0.75rem', fontSize: '1.5rem' }}>
                {loadingStep === 0 && 'Building your kit...'}
                {loadingStep === 1 && 'Searching our product catalog...'}
                {loadingStep >= 2 && 'Matching products to your list...'}
              </h2>
              <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
                Sit tight while we build your kit.<br/>It may take a few moments.
              </p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Upload an image of your school supply list to get started.
                </div>
                <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                  We'll use AI to read the list and instantly build your kit!
                </div>
              </div>

              <div
                onClick={() => inputRef.current.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${file ? '#10b981' : '#c7d2fe'}`,
                  borderRadius: 16, padding: '2.5rem 1rem', cursor: 'pointer',
                  background: file ? '#f0fdf4' : '#f8faff',
                  transition: 'all 0.2s', marginBottom: '1.5rem'
                }}
              >
                <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }}
                  onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
                
                {file ? (
                  <div>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📄</div>
                    <div style={{ fontWeight: 700, color: '#065f46', fontSize: '1rem' }}>{file.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 4 }}>Click to change file</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🖼️</div>
                    <div style={{ fontWeight: 700, color: '#4f46e5', fontSize: '1.1rem' }}>Drag & Drop Your File Here</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 4 }}>or click to browse</div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 12 }}>Supports: JPG, PNG, PDF</div>
                  </div>
                )}
              </div>

              {error && (
                <div style={{ color: '#dc2626', fontSize: '0.85rem', padding: '0.75rem', background: '#fef2f2', borderRadius: 8, marginBottom: '1.5rem' }}>
                  {error}
                </div>
              )}

              <button 
                onClick={handleUpload}
                disabled={!file}
                style={{
                  width: '100%', padding: '1rem', background: file ? '#84cc16' : '#d1d5db',
                  color: file ? '#fff' : '#6b7280', border: 'none', borderRadius: 999,
                  fontSize: '1rem', fontWeight: 800, cursor: file ? 'pointer' : 'not-allowed',
                  transition: 'background 0.2s', boxShadow: file ? '0 4px 14px rgba(132, 204, 22, 0.4)' : 'none'
                }}
              >
                Start Upload
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadListModal;
